import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { EnterprisePageHeader } from '@/components/enterprise/EnterprisePageHeader';
import { useAuthStore } from '@/store/authStore';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import {
  EpcWbsLibraryCommandBar,
  type LibraryViewMode,
} from '@/components/epc/libraries/EpcWbsLibraryCommandBar';
import { EpcWbsLibraryRail } from '@/components/epc/libraries/EpcWbsLibraryRail';
import { EpcWbsLibraryPackageCatalogue } from '@/components/epc/libraries/EpcWbsLibraryPackageCatalogue';
import { EpcWbsLibraryPackageWorkbench } from '@/components/epc/libraries/EpcWbsLibraryPackageWorkbench';
import { EpcCompanyCbsDrawer } from '@/components/epc/libraries/EpcCompanyCbsDrawer';
import { EpcWbsLibraryImportWizard } from '@/components/epc/libraries/EpcWbsLibraryImportWizard';
import { EpcWbsLibraryPromoteWizard } from '@/components/epc/libraries/EpcWbsLibraryPromoteWizard';
import { EpcWbsLibraryPackageCompareDrawer } from '@/components/epc/libraries/EpcWbsLibraryPackageCompareDrawer';
import styles from './EpcWbsLibraryWorkspace.module.css';

const { Paragraph } = Typography;

function canManageLibrary(permissions?: string[]) {
  if (!permissions?.length) return true;
  return (
    permissions.includes('epc.library.manage') ||
    permissions.includes('epc.admin') ||
    permissions.includes('epc.tender.manage')
  );
}

export const EpcWbsLibraryWorkspace: React.FC = () => {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const readOnly = !canManageLibrary(user?.permissions);

  const libraryId = searchParams.get('library') || undefined;
  const packageId = searchParams.get('package') || undefined;
  const nodeId = searchParams.get('node') || undefined;
  const focusMode = searchParams.get('focus') === '1';

  const [search, setSearch] = useState('');
  const [railSearch, setRailSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [readinessFilter, setReadinessFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('code');
  const [compareOpen, setCompareOpen] = useState(false);
  const [libModalOpen, setLibModalOpen] = useState(false);
  const [libEditOpen, setLibEditOpen] = useState(false);
  const [pkgModalOpen, setPkgModalOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<any | null>(null);
  const [cbsOpen, setCbsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [libForm] = Form.useForm();
  const [libEditForm] = Form.useForm();
  const [pkgForm] = Form.useForm();

  const setSelection = useCallback(
    (next: { library?: string; package?: string; node?: string; focus?: boolean | null }) => {
      const p = new URLSearchParams(searchParams);
      if (next.library) p.set('library', next.library);
      else p.delete('library');
      if (next.package) p.set('package', next.package);
      else p.delete('package');
      if (next.node) p.set('node', next.node);
      else p.delete('node');
      if (next.focus === true) p.set('focus', '1');
      else if (next.focus === false || next.focus === null) p.delete('focus');
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const toggleFocusMode = useCallback(() => {
    const p = new URLSearchParams(searchParams);
    if (p.get('focus') === '1') p.delete('focus');
    else p.set('focus', '1');
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const librariesQ = useQuery({
    queryKey: ['epc-wbs-libraries'],
    queryFn: () => epcWbsLibraryService.listLibraries(),
  });

  const libraries = useMemo(() => {
    let rows = Array.isArray(librariesQ.data) ? librariesQ.data : [];
    if (statusFilter !== 'all') {
      rows = rows.filter((l: any) => (l.status || 'Draft') === statusFilter);
    }
    return rows;
  }, [librariesQ.data, statusFilter]);

  const selectedLibrary = useMemo(
    () => libraries.find((l: any) => String(l.id) === libraryId),
    [libraries, libraryId]
  );

  const packagesQ = useQuery({
    queryKey: ['epc-wbs-lib-packages', libraryId],
    queryFn: () => epcWbsLibraryService.listPackages(libraryId!),
    enabled: !!libraryId,
  });

  const packages = Array.isArray(packagesQ.data) ? packagesQ.data : [];
  const selectedPackage = packages.find((p: any) => String(p.id) === packageId);

  const nodesQ = useQuery({
    queryKey: ['epc-wbs-lib-nodes', packageId],
    queryFn: () => epcWbsLibraryService.listPackageNodes(packageId!),
    enabled: !!packageId,
  });

  const seedM = useMutation({
    mutationFn: () => epcWbsLibraryService.seedDefaultLibrary(),
    onSuccess: (data: any) => {
      message.success('Sample catalogue seeded');
      void qc.invalidateQueries({ queryKey: ['epc-wbs-libraries'] });
      if (data?.libraryId) setSelection({ library: String(data.libraryId) });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const createLibM = useMutation({
    mutationFn: (v: Record<string, unknown>) => epcWbsLibraryService.createLibrary(v),
    onSuccess: (data: any) => {
      message.success('Library created');
      setLibModalOpen(false);
      libForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-wbs-libraries'] });
      if (data?.id) setSelection({ library: String(data.id) });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchLibM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.patchLibrary(libraryId!, v),
    onSuccess: () => {
      message.success('Library updated');
      setLibEditOpen(false);
      void qc.invalidateQueries({ queryKey: ['epc-wbs-libraries'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const deleteLibM = useMutation({
    mutationFn: () => epcWbsLibraryService.deleteLibrary(libraryId!),
    onSuccess: () => {
      message.success('Library archived');
      setSelection({});
      void qc.invalidateQueries({ queryKey: ['epc-wbs-libraries'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const createPkgM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.createPackage(libraryId!, v),
    onSuccess: (data: any) => {
      message.success('Package created');
      setPkgModalOpen(false);
      pkgForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
      if (data?.id) setSelection({ library: libraryId!, package: String(data.id) });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchPkgM = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      epcWbsLibraryService.patchPackage(String(editingPkg?.id), v),
    onSuccess: () => {
      message.success('Package updated');
      setEditingPkg(null);
      pkgForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const openEditLibrary = () => {
    if (!selectedLibrary) return;
    libEditForm.setFieldsValue({
      name: selectedLibrary.name,
      description: selectedLibrary.description,
      disciplineCode: selectedLibrary.discipline_code,
      status: selectedLibrary.status || 'Draft',
      versionLabel: selectedLibrary.version_label,
    });
    setLibEditOpen(true);
  };

  const openEditPackage = (pkg: any) => {
    setEditingPkg(pkg);
    pkgForm.setFieldsValue({
      code: pkg.code,
      name: pkg.name,
      description: pkg.description,
    });
    setPkgModalOpen(true);
  };

  const viewMode: LibraryViewMode = packageId
    ? 'package'
    : libraryId
      ? 'catalogue'
      : 'browse';

  const immersivePackage = viewMode === 'package';

  const shell = (
    <div className={styles.workspace}>
        {!immersivePackage ? (
          <EnterprisePageHeader
            documentLabel="WBS Libraries"
            documentNumber={selectedLibrary ? String(selectedLibrary.code) : undefined}
            statusTag={
              selectedLibrary ? (
                <Tag color={selectedLibrary.status === 'Active' ? 'success' : 'default'}>
                  {selectedLibrary.status || 'Draft'}
                </Tag>
              ) : (
                <Tag color="blue">EST3 Package Catalogue</Tag>
              )
            }
            subtitle={
              selectedLibrary ? (
                selectedLibrary.name
              ) : (
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Reusable work packages with WBS structure and CBS cost templates. Plug into
                  estimate BOQ lines in one action.
                </Paragraph>
              )
            }
            facts={
              selectedLibrary ? (
                <Space wrap size="middle">
                  <span>{selectedLibrary.package_count ?? packages.length} packages</span>
                  {selectedLibrary.discipline_code ? (
                    <span>Discipline: {selectedLibrary.discipline_code}</span>
                  ) : null}
                  {selectedLibrary.version_label ? (
                    <span>Version: {selectedLibrary.version_label}</span>
                  ) : null}
                </Space>
              ) : undefined
            }
            primaryActions={
              selectedLibrary && !readOnly ? (
                <Space>
                  <Button icon={<EditOutlined />} onClick={openEditLibrary}>
                    Edit catalogue
                  </Button>
                  <Popconfirm title="Archive this catalogue?" onConfirm={() => deleteLibM.mutate()}>
                    <Button danger loading={deleteLibM.isPending}>
                      Archive
                    </Button>
                  </Popconfirm>
                </Space>
              ) : undefined
            }
          />
        ) : null}

        <EpcWbsLibraryCommandBar
          mode={viewMode}
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          readinessFilter={readinessFilter}
          onReadinessFilterChange={viewMode === 'catalogue' ? setReadinessFilter : undefined}
          sortFilter={sortFilter}
          onSortFilterChange={viewMode === 'catalogue' ? setSortFilter : undefined}
          onComparePackages={viewMode === 'catalogue' ? () => setCompareOpen(true) : undefined}
          canComparePackages={packages.length > 1}
          readOnly={readOnly}
          railCollapsed={immersivePackage || railCollapsed}
          onToggleRail={
            libraryId ? () => setRailCollapsed((c) => !c) : undefined
          }
          onNewLibrary={() => setLibModalOpen(true)}
          onNewPackage={() => {
            setEditingPkg(null);
            pkgForm.resetFields();
            setPkgModalOpen(true);
          }}
          onSeed={() => seedM.mutate()}
          onImportPackage={() => setImportOpen(true)}
          onPromote={() => setPromoteOpen(true)}
          onOpenCbs={() => setCbsOpen(true)}
          seeding={seedM.isPending}
          hasLibrarySelected={!!libraryId}
        />

        <div
          className={`${styles.body} ${immersivePackage ? styles.bodyImmersive : ''}`}
        >
          <aside
            className={`${styles.rail} ${immersivePackage || railCollapsed ? styles.railCollapsed : ''}`}
          >
            <EpcWbsLibraryRail
              libraries={libraries}
              loading={librariesQ.isLoading}
              selectedLibraryId={libraryId}
              onSelect={(id) => setSelection({ library: id })}
              search={railSearch}
              onSearchChange={setRailSearch}
            />
          </aside>

          <main className={styles.main}>
            {!libraryId ? (
              <Paragraph type="secondary">Select a catalogue from the left rail to begin.</Paragraph>
            ) : packageId && selectedPackage ? (
              <EpcWbsLibraryPackageWorkbench
                libraryId={libraryId}
                libraryCode={selectedLibrary?.code}
                packageId={packageId}
                packageCode={selectedPackage.code}
                packageName={selectedPackage.name}
                nodes={Array.isArray(nodesQ.data) ? nodesQ.data : []}
                nodesLoading={nodesQ.isLoading}
                selectedNodeId={nodeId}
                nodeSearch={search}
                onSelectNode={(id) =>
                  id
                    ? setSelection({ library: libraryId, package: packageId, node: id })
                    : setSelection({ library: libraryId, package: packageId })
                }
                onBack={() => setSelection({ library: libraryId, focus: focusMode ? true : null })}
                onNodesChanged={() => {
                  void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-nodes', packageId] });
                  void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-readiness', packageId] });
                  void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-cost-templates', packageId] });
                  void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-analytics', libraryId] });
                }}
                onPackageDuplicated={(newId) =>
                  setSelection({ library: libraryId, package: newId })
                }
                readOnly={readOnly || String(selectedPackage.status || '') === 'Published'}
                focusMode={focusMode}
                onToggleFocus={toggleFocusMode}
              />
            ) : (
              <EpcWbsLibraryPackageCatalogue
                libraryId={libraryId}
                packages={packages}
                loading={packagesQ.isLoading}
                readOnly={readOnly}
                search={search}
                readinessFilter={readinessFilter}
                sortFilter={sortFilter}
                selectedPackageId={packageId}
                onSelectPackage={(id) => setSelection({ library: libraryId, package: id })}
                onCreatePackage={() => {
                  setEditingPkg(null);
                  pkgForm.resetFields();
                  setPkgModalOpen(true);
                }}
                onEditPackage={openEditPackage}
                onSeed={() => seedM.mutate()}
              />
            )}
          </main>
        </div>
      </div>
  );

  return (
    <div
      className={focusMode && immersivePackage ? styles.focusShell : undefined}
      style={focusMode && immersivePackage ? undefined : { padding: 16 }}
    >
      {shell}

      <Modal
        title="New catalogue"
        open={libModalOpen}
        onCancel={() => setLibModalOpen(false)}
        onOk={() => libForm.submit()}
        confirmLoading={createLibM.isPending}
      >
        <Form form={libForm} layout="vertical" onFinish={(v) => createLibM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="disciplineCode" label="Discipline">
            <Input placeholder="e.g. CIV, MEC" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="Draft">
            <Select options={[{ value: 'Draft' }, { value: 'Active' }]} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Edit catalogue"
        open={libEditOpen}
        onClose={() => setLibEditOpen(false)}
        width={400}
        extra={
          <Button type="primary" loading={patchLibM.isPending} onClick={() => libEditForm.submit()}>
            Save
          </Button>
        }
      >
        <Form form={libEditForm} layout="vertical" onFinish={(v) => patchLibM.mutate(v)}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="disciplineCode" label="Discipline">
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={[{ value: 'Draft' }, { value: 'Active' }]} />
          </Form.Item>
          <Form.Item name="versionLabel" label="Version">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title={editingPkg ? 'Edit package' : 'New package'}
        open={pkgModalOpen}
        onCancel={() => {
          setPkgModalOpen(false);
          setEditingPkg(null);
        }}
        onOk={() => pkgForm.submit()}
        confirmLoading={createPkgM.isPending || patchPkgM.isPending}
      >
        <Form
          form={pkgForm}
          layout="vertical"
          onFinish={(v) => {
            if (editingPkg) patchPkgM.mutate(v);
            else createPkgM.mutate(v);
          }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input disabled={!!editingPkg} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <EpcCompanyCbsDrawer open={cbsOpen} onClose={() => setCbsOpen(false)} readOnly={readOnly} />

      <EpcWbsLibraryImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        libraryId={libraryId || ''}
        onCompleted={() => {
          setImportOpen(false);
          void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
        }}
      />

      <EpcWbsLibraryPromoteWizard
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        defaultLibraryId={libraryId}
        onCompleted={() => {
          setPromoteOpen(false);
          void qc.invalidateQueries({ queryKey: ['epc-wbs-lib-packages'] });
        }}
      />

      {libraryId ? (
        <EpcWbsLibraryPackageCompareDrawer
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          packages={packages}
        />
      ) : null}
    </div>
  );
};

export default EpcWbsLibraryWorkspace;
